import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { DatePicker } from "antd";
import { LoadingIcon, TopDropdown } from "../../../../components";
import { getGreenhouses } from "../../../../utils/api";
import { UserContext } from "../../../../context/UserContext";
import { useLocalStorage } from "../../../../hooks";

dayjs.extend(isoWeek);

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  setCurrentMetric,
  setMetricType,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentVariety,
  setCurrentVariety,
  varietyData,
  setVarietyData,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentDate,
  setCurrentDate,
  setMetricData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  disabledDate,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [varietyLabel, setVarietyLabel] = useState("Variety");
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  // -------------------------------------------------------
  // Fetch greenhouses from API
  // -------------------------------------------------------
  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    const res = await getGreenhouses(orgId, siteId, cropId);
    const greenhouseList = Array.isArray(res?.data?.data)
      ? res.data.data
      : [];
    const data = greenhouseList.map((greenhouse) => ({
      name: greenhouse.name,
      id: greenhouse.id,
      varieties: greenhouse.varieties || [],
      metadata: greenhouse.metadata || {},
    }));
    setGreenhouseData(data);
    setGreenhouseLoading(false);
  };

  // -------------------------------------------------------
  // Handle date changes (week-based)
  // -------------------------------------------------------
  const handleDateChange = (date) => {
    if (date) {
      const mondayOfSelectedWeek = date.isoWeekday(1);
      setCurrentDate(mondayOfSelectedWeek.format("YYYY-MM-DD"));
    } else {
      setCurrentDate(null);
    }
  };

  // -------------------------------------------------------
  // Whenever org/site/crop changes => fetch greenhouse
  // -------------------------------------------------------
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      fetchGreenhouses(currentOrg, currentSite.id, currentCrop.id);
    }
  }, [currentSite, currentOrg, currentCrop]);

  // -------------------------------------------------------
  // If siteMetadata has greenhouse_name/variety_name, override labels
  // -------------------------------------------------------
  useEffect(() => {
    if (siteMetadata && Object.keys(siteMetadata).length > 0) {
      if (siteMetadata?.greenhouse_name) {
        setGreenHouseLabel(siteMetadata.greenhouse_name);
      }
      if (siteMetadata?.variety_name) {
        setVarietyLabel(siteMetadata.variety_name);
      }
    }
  }, [siteMetadata]);

  useEffect(() => {
    if (
      currentSite &&
      currentCrop &&
      currentGreenhouse &&
      currentGreenhouse.length > 0
    ) {
      setSiteMetadata(currentSite.metadata[currentCrop.name] || {});
      const metrics = currentSite.metadata[currentCrop.name]?.metrics
        ? Object.entries(currentSite.metadata[currentCrop.name].metrics).map(
            (obj) => ({
              type: obj[0],
              metrics: obj[1],
            }),
          )
        : [];
      setMetricData(metrics);
    } else {
      setMetricData([]);
    }
  }, [currentSite, currentCrop, currentGreenhouse]);

  useEffect(() => {
    if (greenhouseData && greenhouseData?.length) {
      const params = new URLSearchParams(searchParams);
      const greenhouseParam = params.get("greenhouse");

      let selectedGreenhouseIDs = [];
      if (greenhouseParam) {
        selectedGreenhouseIDs = greenhouseParam.split(",");
      } else if (!settingDefaults.greenhouse) {
        const storedGHArr = defaultDropdown?.[currentOrg]?.greenhouses;
        if (storedGHArr && storedGHArr.length > 0) {
          selectedGreenhouseIDs = storedGHArr;
        } else {
          selectedGreenhouseIDs = greenhouseData.map((gh) => gh.id);
        }
        setSettingDefaults((prev) => ({ ...prev, greenhouse: true }));
      } else {
        selectedGreenhouseIDs = greenhouseData.map((gh) => gh.id);
      }

      // Filter greenhouseData by those IDs
      let selectedGreenhouses = greenhouseData.filter((gh) =>
        selectedGreenhouseIDs.includes(gh.id),
      );

      if (selectedGreenhouses.length === 0) {
        selectedGreenhouses = greenhouseData;
      }

      setCurrentGreenhouse(selectedGreenhouses);

      // Now combine all the varieties from these selected greenhouses
      const combinedVarieties = selectedGreenhouses.flatMap(
        (gh) => gh.varieties || [],
      );
      // create unique list
      const uniqueVarieties = Array.from(
        new Map(combinedVarieties.map((v) => [v.id, v])).values(),
      );
      setVarietyData(uniqueVarieties);
    } else {
      setCurrentGreenhouse([]);
      setVarietyData([]);
    }
  }, [greenhouseData]);

  // -------------------------------------------------------
  // Whenever currentGreenhouse changes => store IDs in param + localStorage.
  // Also recalc varietyData.
  // -------------------------------------------------------
  useEffect(() => {
    if (currentGreenhouse && currentGreenhouse.length > 0) {
      let existingParams = new URLSearchParams(searchParams);
      const greenhouseIDs = currentGreenhouse.map((gh) => gh.id).join(",");
      existingParams.set("greenhouse", greenhouseIDs);
      setSearchParams(existingParams);

      // update localStorage
      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouses: currentGreenhouse.map((gh) => gh.id),
        },
      });

      // recalc varietyData from these GH
      const combinedVarieties = currentGreenhouse.flatMap(
        (gh) => gh.varieties || [],
      );
      const uniqueVarieties = Array.from(
        new Map(combinedVarieties.map((v) => [v.id, v])).values(),
      );
      handleVarietyDefaultSelection(uniqueVarieties);
    } else {
      setVarietyData([]);
      setCurrentVariety([]);
    }
  }, [currentGreenhouse]);

  // -------------------------------------------------------
  // handle variety default selection: URL param or local storage
  // -------------------------------------------------------
  const handleVarietyDefaultSelection = (varieties) => {
    setVarietyData(varieties);

    if (!varieties?.length) {
      setCurrentVariety([]);
      return;
    }

    let params = new URLSearchParams(searchParams);
    let varietyParam = params.get("variety");

    let selectedVarietyIDs = [];
    if (varietyParam) {
      selectedVarietyIDs = varietyParam.split(",");
    } else if (!settingDefaults.variety) {
      const storedVarArr = defaultDropdown?.[currentOrg]?.varieties;
      if (storedVarArr && storedVarArr.length > 0) {
        selectedVarietyIDs = storedVarArr;
      } else {
        selectedVarietyIDs = varieties.map((v) => v.id);
      }
      setSettingDefaults((prev) => ({ ...prev, variety: true }));
    } else {
      selectedVarietyIDs = varieties.map((v) => v.id);
    }

    let selected = varieties.filter((v) => selectedVarietyIDs.includes(v.id));
    if (selected.length === 0) {
      selected = varieties;
    }
    setCurrentVariety(selected);
  };

  // -------------------------------------------------------
  // Whenever currentVariety changes => update URL & localStorage
  // -------------------------------------------------------
  useEffect(() => {
    if (currentVariety && currentVariety.length > 0) {
      let existingParams = new URLSearchParams(searchParams);
      const varietyIDs = currentVariety.map((v) => v.id).join(",");
      existingParams.set("variety", varietyIDs);
      setSearchParams(existingParams);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          varieties: currentVariety.map((v) => v.id),
        },
      });
    }
  }, [currentVariety]);

  // -------------------------------------------------------
  // Handle metric defaults from site metadata
  // -------------------------------------------------------
  useEffect(() => {
    if (currentSite && currentCrop) {
      let val =
        currentSite.metadata?.[currentCrop.name]?.default_metrics?.[0] || null;
      setMetricType(val);
    } else {
      setMetricType(null);
    }
  }, [currentSite, currentCrop]);

  useEffect(() => {
    if (currentSite && currentCrop) {
      let val =
        currentSite.metadata?.[currentCrop.name]?.default_metrics?.[1] || null;
      setCurrentMetric(val);
    } else {
      setCurrentMetric(null);
    }
  }, [currentSite, currentCrop]);

  // -------------------------------------------------------
  // Dropdown loading logic
  // -------------------------------------------------------
  useEffect(() => {
    setDropdownLoading(
      orgLoading || siteLoading || cropLoading || greenhouseLoading,
    );
  }, [
    orgLoading,
    siteLoading,
    cropLoading,
    greenhouseLoading,
    setDropdownLoading,
  ]);

  // -------------------------------------------------------
  //  Multi-select callbacks (ID-based) for GH & Variety
  // -------------------------------------------------------
  const handleGreenhouseChange = (ghIds) => {
    const selectedGHs = greenhouseData.filter((gh) => ghIds.includes(gh.id));
    setCurrentGreenhouse(selectedGHs);
  };

  const handleVarietyChange = (varietyIds) => {
    const selectedVarieties = varietyData.filter((v) =>
      varietyIds.includes(v.id),
    );
    setCurrentVariety(selectedVarieties);
  };

  return (
    <>
      {orgLoading || siteLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 sm:mb-4 dropdowns !font-MuseoSans">
          {/* Org: ID-based */}
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[200px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          {/* Site: ID-based */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[200px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop: ID-based */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          {/* Greenhouse: multiple‐select, ID‐based */}
          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            mode="multiple"
            maxTagCount={2}
            val={currentGreenhouse?.map((gh) => gh.id) ?? []}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={handleGreenhouseChange}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={greenhouseData?.map((gh) => ({
              value: gh.id,
              label: gh.name,
            }))}
          />

          {/* Variety: multiple‐select, ID‐based */}
          <TopDropdown
            label={`Select ${varietyLabel}:`}
            mode="multiple"
            maxTagCount={2}
            val={currentVariety?.map((v) => v.id) ?? []}
            placeholder={`Select ${varietyLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={handleVarietyChange} // returns array of IDs
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={varietyData?.map((v) => ({
              value: v.id,
              label: v.name,
            }))}
          />

          {/* DatePicker (Week-based) */}
          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Week:
            </div>
            <DatePicker
              picker="week"
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[200px] sm:mb-2"
              disabledDate={disabledDate}
              onChange={handleDateChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
